/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { flexbox, layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useSelector, useDispatch } from 'react-redux';
import { getTaxInterviewsRequest } from 'redux-api/action/taxInterviews';
import selectTaxInterviews from 'redux-api/reselect/taxInterviews';
import ContentLoader from 'theme/content-loader';
import TaxInterviewCard from 'theme/tax-interview-card';
import Text from 'theme/text';
import BtnLink from 'theme/buttons/btn-link';
import Plus from 'theme/icons/plus';
import translations from 'translations';

const Section = styled.div`
  width: 100%;
  max-width: 2100px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${flexbox}
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: ${themeGet('space.m')}px 0;
  padding: ${themeGet('space.s')}px;
  background: ${themeGet('colors.primary_100')};
  border: 1px solid ${themeGet('colors.primary_200')};
  border-radius: 12px;
  overflow: auto;

  ${layout}
`;

const WrapEmpty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 220px;
`;

function TaxInterviews() {
  const dispatch = useDispatch();

  const { push } = useRouter();

  const { languageCode } = useSelector((state) => state.config);
  const { isWaiting } = useSelector((state) => state.billing);
  const payload = useSelector(selectTaxInterviews);

  useEffect(() => {
    dispatch(
      getTaxInterviewsRequest({
        page: 0,
        pageSize: 10,
      }),
    );
  }, [dispatch]);

  const handleOnClickNew = useCallback(() => push('/billing/tax-interview'), [push]);

  return (
    <Section>
      <Row justifyContent="space-between">
        <Row>
          <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600">
            {translations[languageCode]['Tax Interviews']}
          </Text>

          <Text pl="xxs" fontSize="h3" lineHeight="h3" fontWeight="bold" color="primary_400">
            {payload.totalCount}
          </Text>
        </Row>

        <Row>
          <BtnLink
            label={translations[languageCode]['Create new']}
            icon={<Plus />}
            onClick={handleOnClickNew}
          />
        </Row>
      </Row>

      <CardsContainer minHeight="148px">
        {isWaiting ? (
          <ContentLoader
            width={808}
            height={148}
            lines={[
              {
                x: 0,
                y: 0,
                rx: 5,
                ry: 5,
                width: 400,
                height: 148,
              },
              {
                x: 408,
                y: 0,
                rx: 5,
                ry: 5,
                width: 400,
                height: 148,
              },
            ]}
          />
        ) : payload.totalCount === 0 ? (
          <WrapEmpty>
            <Text
              pl="xxs"
              fontSize="pSmall"
              lineHeight="pSmall"
              fontWeight="normal"
              color="primary_600"
            >
              {translations[languageCode]["You don't have a “Tax Interview” yet"]}
            </Text>
          </WrapEmpty>
        ) : (
          payload.data.map((taxInterview) => (
            <TaxInterviewCard
              key={taxInterview.id}
              style={{
                mr: 'xs',
                maxWidth: '400px',
                border: '1px solid',
                borderColor: 'primary_200',
              }}
              {...taxInterview}
              languageCode={languageCode}
            />
          ))
        )}
      </CardsContainer>
    </Section>
  );
}

export default TaxInterviews;
